import { BetSlipResult } from "@api-types/agency/bet-slip";
import { BetSlipErrorCodesEnum } from "@state/bet-slip/BetSlipErrorCodesEnum";
import { DocumentExpirationErrorCodes } from "@api-types/documentExpiration/documentExpiration";
import { AnnualIncomeErrorCodes } from "@api-types/user/AnnualIncomeErrorCodes";
import { BetSlipInteractionTypeEnum } from "@data-types/analytics-tracker/BetSlipInteractionTypeEnum";
import { LazyImportWithLoadFailHandle as lazy } from "@lib/lazy-import-with-guard/LazyImportWithGuard";
import { Bet } from "@models";
import ModalService from "@services/modals/ModalService";
import { localizationService } from "@state";
import { LoaderStore } from "@state/stores/common";
import type { RootStore } from "@state/stores/types";
import { getCurrentCulture } from "@utils";
import { analyticsTracker } from "@utils/analytics";
import { DateTime } from "luxon";
import { action, observable, runInAction } from "mobx";

const loadFailPath = `/${getCurrentCulture()}/app-update`;

// prettier-ignore
const BetSlipApiService = lazy(loadFailPath, ()=>import("@api-services/agency/BetSlipApiService"));

export class BetCancelStore {
	loader: LoaderStore;
	myBetsStore: Store;
	rootStore: RootStore;
	constructor(rootStore: RootStore, myBetsStore: Store) {
		this.rootStore = rootStore;
		this.myBetsStore = myBetsStore;

		// Helper stores
		this.loader = new LoaderStore();

		this.getResponseErrorMessage = this.getResponseErrorMessage.bind(this);
	}

	//#region observables

	@observable isCancelPopUpOpen = false;
	@observable betToCancel: Bet | null = null;
	@observable betCancelMessage:
		| string
		| object
		| (string | object)[]
		| undefined
		| null = null;

	//#endregion observables

	//#region bet cancel

	@action.bound
	async onBetCancelConfirm() {
		if (this.betToCancel) {
			try {
				this.loader.suspend();

				const response: BetSlipResult = await (
					await BetSlipApiService
				).default.putCancelBet(
					this.betToCancel.id,
					this.rootStore.userAuthStore.mapUserIdToUserContext(
						this.betToCancel?.userId
					)
				);

				this.rootStore.userAuthStore.resolveBalance();

				// Triggers bets refetch for any event from this bet slip if it's MyBetsTab is currently open
				if (this.rootStore.platform === "web") {
					const myBetsViewStore = this.rootStore.myBetsViewStore;

					response.betSlip.betSlipOffers.forEach((offer) =>
						myBetsViewStore.syncEventBetsTab(offer.eventId)
					);
				}

				this.myBetsStore.refresh();
				this.loader.resume();

				runInAction(() => {
					this.betCancelMessage =
						this.getResponseErrorMessage(response);

					this.rootStore.notificationStore.success(
						localizationService.t(
							"mybets:BET_CANCEL.CANCEL_SUCCESS"
						)
					);

					if (this.isCancelPopUpOpen) {
						this.isCancelPopUpOpen = false;
					}
				});

				analyticsTracker.trackBetSlipCancelInteraction(
					BetSlipInteractionTypeEnum.ConfirmCancel
				);
			} catch (error) {
				console.error(error);

				this.loader.resume();

				runInAction(() => {
					this.betCancelMessage = error?.data
						? this.getResponseErrorMessage(error?.data)
						: localizationService.t(
								"mybets:BET_CANCEL.CANCEL_FAILED"
							);
				});
			}
		}
	}

	@action.bound
	getResponseErrorMessage(response: BetSlipResult) {
		if (
			response.validationErrors != null &&
			response.validationErrors.length > 0
		) {
			return response.validationErrors[0].errorMessage;
		}

		if (
			response.betSlipProcessingResponses != null &&
			response.betSlipProcessingResponses.length > 0
		) {
			if (response.betSlipProcessingResponses[0].errorCode === 400262) {
				response.betSlipProcessingResponses[0].errorMessage =
					response.betSlipProcessingResponses[0].errorMessage.replace(
						"{0}",
						DateTime.fromISO(
							//@ts-expect-error
							// prettier-ignore
							response.betSlipProcessingResponses[0].previouslyClosedAt
						).toFormat("f")
					);
				response.betSlipProcessingResponses[0].errorMessage =
					response.betSlipProcessingResponses[0].errorMessage.replace(
						"{1}",
						DateTime.fromISO(
							//@ts-expect-error
							response.betSlipProcessingResponses[0].nextOpening
						).toFormat("f")
					);
			}
			if (
				response.betSlipProcessingResponses[0].errorCode ==
					BetSlipErrorCodesEnum.BetSlipCancelLUGASUnavailable ||
				response.betSlipProcessingResponses[0].errorCode ==
					BetSlipErrorCodesEnum.BetSlipCancelOASISUnavailable
			) {
				this.rootStore.notificationStore.error(
					response.betSlipProcessingResponses[0].errorMessage
				);
				this.onExitBetCancel();
				return;
			}

			if (
				response.betSlipProcessingResponses[0].errorCode ==
					DocumentExpirationErrorCodes.UserDocumentExpirationDateExpired ||
				response.betSlipProcessingResponses[0].errorCode ==
					DocumentExpirationErrorCodes.UserDocumentExpirationDateNotSet
			) {
				this.rootStore.accountVerificationStore.showPopup(
					"account-settings/document-expiration",
					"DOCUMENT_EXPIRATION.TITLE",
					response.betSlipProcessingResponses[0].errorMessage
				);
				this.onExitBetCancel();
				return;
			}
			if (
				response.betSlipProcessingResponses[0].errorCode ==
				BetSlipErrorCodesEnum.WalletActivationGracePeriod
			) {
				this.rootStore.walletGracePeriodPopupStore.setMessage(
					response.betSlipProcessingResponses[0].errorMessage
				);
				this.rootStore.walletGracePeriodPopupStore.showPopup();
				this.onExitBetCancel();
				return;
			}
			if (
				response.betSlipProcessingResponses[0].errorCode ==
				AnnualIncomeErrorCodes.AnnualIncomeUpdateRequired
			) {
				this.rootStore.accountVerificationStore.showPopup(
					"account-settings/personal-data",
					"COMMON.NAVIGATION.PERSONAL_DATA",
					response.betSlipProcessingResponses[0].errorMessage
				);
				this.onExitBetCancel();
				return;
			}

			return response.betSlipProcessingResponses[0].errorMessage;
		}

		if (response.betSlip != null) {
			return localizationService.t("mybets:BET_CANCEL.CANCEL_SUCCESS");
		}

		return localizationService.t("mybets:BET_CANCEL.CANCEL_FAILED");
	}

	@action.bound
	onBetCancel(bet: Bet) {
		ModalService.onOpen();
		this.isCancelPopUpOpen = true;
		this.betToCancel = bet;
	}

	@action.bound
	onExitBetCancel() {
		this.isCancelPopUpOpen = false;
		this.betToCancel = null;
		this.betCancelMessage = null;
	}

	@action.bound
	onBetCancelBetDetailsModal(bet: Bet) {
		this.betToCancel = bet;
	}

	@action.bound
	onExitBetCancelBetDetailsModal() {
		this.betToCancel = null;
		this.betCancelMessage = null;
	}

	//#endregion bet cancel

	//#region disposers

	@action.bound
	onDispose() {
		this.onExitBetCancel();
	}

	//#endregion disposers
}

type Store = {
	refresh: () => void;
};
